import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';
import { JAVA_SERVICE_URL, JAVA_SERVICE_ENDPOINTS } from '../endpoints';
import useAuthStore from '../../../store/useAuthStore';
import { API_CLIENT_ID } from '../constants';
import { queryClient } from 'config/queryClient';
import { servicesQueryKeys } from '../query-keys';
import { CompanyType } from 'hooks/useCompanyQueries';

/**
 * Refreshes the access token using the current refresh token.
 * Resets state and redirects to login if refresh fails.
 */
const refreshAccessToken = async () => {
  const { refreshToken, resetState } = useAuthStore.getState();

  if (!refreshToken) {
    resetState();
    window.location.href = '/';
    return '';
  }

  try {
    const response = await queryClient.fetchQuery({
      queryKey: servicesQueryKeys.getNewAccessToken({ refresh_token: refreshToken }),
      queryFn: () =>
        apiClient().post(JAVA_SERVICE_ENDPOINTS.REFRESH_TOKEN, {
          refresh_token: refreshToken,
        }),
    });

    const { auth_token: accessToken = '', refresh_token: newRefreshToken = '' } =
      response?.data || {};

    if (accessToken) {
      useAuthStore.setState({ accessToken, refreshToken: newRefreshToken });
      return accessToken;
    }

    resetState();
    window.location.href = '/';
    return '';
  } catch (error) {
    resetState();
    window.location.href = '/';
    console.warn('Error refreshing token:', error);
    return '';
  }
};

/**
 * Creates a configured Axios instance with response interceptors for token management.
 * @param config - Optional Axios request configuration.
 * @returns Configured Axios client.
 */
const apiClient = (config?: AxiosRequestConfig) => {
  const companyId =
    useAuthStore.getState()?.user?.selectedCompanyId || useAuthStore.getState()?.user?.companyId;
  const instance = axios.create({
    baseURL: JAVA_SERVICE_URL,
    headers: {
      'Client-id': API_CLIENT_ID,
      ...(companyId && {
        'Company-id':
          queryClient.getQueryData<AxiosResponse<CompanyType>>(
            servicesQueryKeys.getCompany({
              companyId:
                useAuthStore.getState()?.user?.selectedCompanyId ||
                useAuthStore.getState()?.user?.companyId,
            }),
          )?.data?.id || companyId,
      }),
    },
    ...config,
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { config: originalRequest, response: errorResponse } = error;

      if (errorResponse) {
        const { status, data } = errorResponse;
        const isTokenExpired =
          status === 401 &&
          JSON.stringify(errorResponse).toLocaleLowerCase().includes('token expired');

        if (isTokenExpired || status === 423) {
          if (status === 423) {
            toast.error(data?.errorPresent ? catchErrorMessage(error) : data);
            useAuthStore.getState().resetState();
            return <Navigate to="/login" replace />;
          }

          const newToken = await refreshAccessToken();

          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
          }
        }
      }

      return Promise.reject(error);
    },
  );

  return instance;
};

export default apiClient;
